var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a, _b, _c, _d, _e, _f, _g, _h, _j;
import { faBell, faUserHeadset } from '@fortawesome/pro-duotone-svg-icons';
import { isNil } from 'lodash';
import { PlanType } from '../../enum';
import { formatNumberByLocale } from '../../util';
import { CHART_KEYS, DAILY_DATA_FIELDS, DAILY_FILTER_KEYS, FREQUENCY_ENUM, INTERVAL_TYPE, METRICS_ENUM, PERIOD_DATA_FIELDS, RANGE_COLORS, SECTION_ENUM, SUMMARY_KEYS, } from './enum';
import { formatDayOfWeek, formattedDate, getOptionFormat } from './selectors';
import { METRIC_COLORS } from './settings/constants';
export var INIT_PERIOD_END = -1;
export var ALL_QUEUES_ID = -1;
export var ALL_QUEUES_ITEM = { uniqueId: -1 };
export var AVERAGE = 'Average';
export var SECTION_DATA = [
    {
        value: SECTION_ENUM.PERFORMANCE,
        icon: faBell,
        style: { minWidth: '8.875rem' },
    },
    {
        value: SECTION_ENUM.STAFFING,
        icon: faUserHeadset,
        style: { minWidth: '6.5rem' },
    },
];
export var PLAN_INTERVALS = [
    FREQUENCY_ENUM.PERIOD,
    FREQUENCY_ENUM.DAILY,
    DAILY_FILTER_KEYS.SPECIFIC_DAY,
];
export var METRIC_LIST = (_a = {},
    _a[SECTION_ENUM.PERFORMANCE] = [
        METRICS_ENUM.WORK_OFFERED_HANDLED,
        METRICS_ENUM.SERVICE_LEVEL,
        METRICS_ENUM.ASA,
        METRICS_ENUM.OCCUPANCY,
        METRICS_ENUM.ABANDONMENT_RATE,
        METRICS_ENUM.AHT,
        METRICS_ENUM.CONCURRENCY,
        METRICS_ENUM.PRODUCTION_HOURS,
        METRICS_ENUM.WORKLOAD_HOURS,
    ],
    _a[SECTION_ENUM.STAFFING] = [
        METRICS_ENUM.REQUIRED_FTE,
        METRICS_ENUM.PRODUCTIVE_FTE,
        METRICS_ENUM.REQUIRED_HEADCOUNT,
        METRICS_ENUM.ATTRITION,
    ],
    _a);
export var ALL_LOCATIONS_ID = -1;
export var NETWORK_ONLY_METRICS = [
    METRICS_ENUM.SERVICE_LEVEL,
    METRICS_ENUM.ASA,
    METRICS_ENUM.OCCUPANCY,
    METRICS_ENUM.ABANDONMENT_RATE,
    METRICS_ENUM.CONCURRENCY,
];
export var FULL_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export var TIME_FORMAT = 'h:mmA';
export var LIMIT_TIMELINE = 12;
export var MAX_QUEUES = 5;
export var DATE_PICKER_FORMAT = 'MMMM DD YYYY';
export var DATE_PICKER_SLASH_FORMAT = 'MM/DD/YYYY';
export var DAILY_MAX = 6.5;
export var PERIOD_MAX = 4.5;
export var MIN_RANGE = -0.5;
export var SUMMARY_BOX_BASE = [
    {
        key: SUMMARY_KEYS.PLANNED,
        formatter: function (metricType, value, actualAttritionMode) {
            return formatNumberByLocale(value, getOptionFormat(metricType, actualAttritionMode));
        },
    },
    {
        key: SUMMARY_KEYS.ACTUAL,
        formatter: function (metricType, value, actualAttritionMode) {
            return formatNumberByLocale(value, getOptionFormat(metricType, actualAttritionMode));
        },
    },
    {
        key: SUMMARY_KEYS.VARIANCE,
        formatter: function (_metricType, value) { return formatNumberByLocale(value, getOptionFormat()); },
    },
];
export var SUMMARY_BOX_THRESHOLD = {
    key: SUMMARY_KEYS.THRESHOLD,
    formatter: function (_metricType, value) { return formatNumberByLocale(value); },
};
export var SUMMARY_BOX_HANDLED = [
    {
        key: SUMMARY_KEYS.HANDLED,
        formatter: function (metricType, value) {
            return formatNumberByLocale(value, getOptionFormat(metricType));
        },
    },
    {
        key: SUMMARY_KEYS.ABANDONMENT_RATE,
        formatter: function (_metricType, value) {
            return formatNumberByLocale(value, getOptionFormat(METRICS_ENUM.ABANDONMENT_RATE));
        },
    },
];
export var SETTING_COLOR_DEFAULT = (_b = {},
    _b[RANGE_COLORS.ACCEPTABLE] = { min: -Infinity, max: Infinity },
    _b[RANGE_COLORS.CAUTION] = { min: -Infinity, max: Infinity },
    _b[RANGE_COLORS.OUTSIDE] = { min: -Infinity, max: Infinity },
    _b);
export var COLOR_DEFAULT_VARIANCE = (_c = {},
    _c[RANGE_COLORS.ACCEPTABLE] = METRIC_COLORS.ACCEPTABLE,
    _c[RANGE_COLORS.CAUTION] = METRIC_COLORS.CAUTION,
    _c[RANGE_COLORS.OUTSIDE] = METRIC_COLORS.OUTSIDE,
    _c);
// DEFINE COLUMNS OF TABLES
export var REPORT_DAILY_COLUMNS = [
    {
        id: DAILY_DATA_FIELDS.DATE,
        type: DAILY_FILTER_KEYS.DAILY_AVERAGE,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.grey[400],
            textDecoration: 'underline',
            color: theme.palette.info.main,
            '& span': {
                cursor: 'pointer',
            },
        }); },
        render: function (key) { return formattedDate(key); },
    },
    {
        id: DAILY_DATA_FIELDS.DAY,
        type: DAILY_FILTER_KEYS.DAILY_AVERAGE,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.grey[400],
        }); },
        render: function (key) { return formatDayOfWeek(key); },
    },
    {
        id: DAILY_DATA_FIELDS.INTERVAL,
        type: DAILY_FILTER_KEYS.SPECIFIC_DAY,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.grey[400],
        }); },
        render: function (key) { return key; },
    },
];
export var REPORT_BASE_COLUMNS = [
    {
        id: DAILY_DATA_FIELDS.PLAN,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.primary.light,
        }); },
        render: function (_, value, metricType, actualAttritionMode) {
            return formatNumberByLocale(value, getOptionFormat(metricType, actualAttritionMode));
        },
    },
    {
        id: DAILY_DATA_FIELDS.ACTUAL,
        actualIndex: 0,
        render: function (_, value, metricType, actualAttritionMode) {
            return formatNumberByLocale(value, getOptionFormat(metricType, actualAttritionMode));
        },
    },
    {
        id: DAILY_DATA_FIELDS.VARIANCE,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.warning.light,
            fontWeight: 'bold',
        }); },
        render: function (_, value) { return formatNumberByLocale(value, getOptionFormat()); },
    },
];
export var REPORT_WORK_OFFERED_HANDLED_COLUMNS = [
    {
        id: DAILY_DATA_FIELDS.PLAN_OFFERED,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.primary.light,
        }); },
        render: function (_, value, metricType) {
            return formatNumberByLocale(value, getOptionFormat(metricType));
        },
    },
    {
        id: DAILY_DATA_FIELDS.ACTUAL_OFFERED,
        actualIndex: 0,
        render: function (_, value, metricType) {
            return formatNumberByLocale(value, getOptionFormat(metricType));
        },
    },
    {
        id: DAILY_DATA_FIELDS.ACTUAL_HANDLED,
        actualIndex: 1,
        render: function (_, value, metricType) {
            return formatNumberByLocale(value, getOptionFormat(metricType));
        },
    },
    {
        id: DAILY_DATA_FIELDS.VARIANCE_OFFERED,
        styled: function (theme) { return ({
            backgroundColor: theme.palette.warning.light,
            fontWeight: 'bold',
        }); },
        render: function (_, value) { return formatNumberByLocale(value, getOptionFormat()); },
    },
    {
        id: DAILY_DATA_FIELDS.ABANDONMENT_RATE,
        styled: function (theme) { return ({
            padding: theme.spacing(1, 0),
            backgroundColor: theme.palette.warning.light,
            fontWeight: 'bold',
        }); },
        render: function (_, value) { return formatNumberByLocale(value, getOptionFormat(METRICS_ENUM.ABANDONMENT_RATE)); },
    },
];
export var MAXIMUM_VALUES = (_d = {},
    _d[METRICS_ENUM.SERVICE_LEVEL] = { min: 0, max: 100 },
    _d[METRICS_ENUM.ASA] = { min: 0, max: 99999 },
    _d[METRICS_ENUM.OCCUPANCY] = { min: 0, max: 100 },
    _d[METRICS_ENUM.AHT] = { min: 1, max: 99999 },
    _d[METRICS_ENUM.CONCURRENCY] = { min: 1, max: 99 },
    _d[METRICS_ENUM.WORK_OFFERED_HANDLED] = { min: 0, max: 9999999 },
    _d[METRICS_ENUM.PRODUCTION_HOURS] = { min: 0, max: 9999999 },
    _d[METRICS_ENUM.WORKLOAD_HOURS] = { min: 0, max: 9999999 },
    _d[METRICS_ENUM.REQUIRED_FTE] = { min: 0, max: 999999 },
    _d[METRICS_ENUM.PRODUCTIVE_FTE] = { min: 0, max: 999999 },
    _d[METRICS_ENUM.REQUIRED_HEADCOUNT] = { min: 0, max: 99999 },
    _d[METRICS_ENUM.ATTRITION] = { min: 0, max: 99.9 },
    _d[METRICS_ENUM.ABANDONMENT_RATE] = { min: 0, max: 100 },
    _d);
export var PERCENTAGES = (_e = {},
    _e[METRICS_ENUM.SERVICE_LEVEL] = {
        decimal: 1,
        suffixDefault: '%',
    },
    _e[METRICS_ENUM.OCCUPANCY] = {
        decimal: 1,
        suffixDefault: '%',
    },
    _e[METRICS_ENUM.ATTRITION] = {
        decimal: 1,
        suffixDefault: '%',
    },
    _e);
export var DECIMALS = (_f = {},
    _f[METRICS_ENUM.CONCURRENCY] = {
        decimal: 1,
    },
    _f[METRICS_ENUM.REQUIRED_FTE] = {
        decimal: 1,
    },
    _f[METRICS_ENUM.PRODUCTIVE_FTE] = {
        decimal: 1,
    },
    _f);
export var METRIC_FORMATTING_OPTIONS = __assign(__assign({}, PERCENTAGES), DECIMALS);
export var DAILY_FILTERS = [DAILY_FILTER_KEYS.DAILY_AVERAGE, DAILY_FILTER_KEYS.SPECIFIC_DAY];
// CHART CONFIG
export var NORMAL_CHART = [
    {
        key: CHART_KEYS.PLAN,
        type: 'bar',
        marker: { color: '#8c84d9' },
        yaxis: 'y',
    },
    {
        key: CHART_KEYS.ACTUAL,
        type: 'bar',
        marker: { color: '#89ca9c' },
        yaxis: 'y',
    },
    {
        key: CHART_KEYS.VARIANCE,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#e88b1e', width: 2, shape: 'spline' },
        yaxis: 'y2',
    },
];
export var WORK_VOLUME_CHART = [
    {
        key: CHART_KEYS.OFFERED,
        type: 'bar',
        marker: { color: '#89ca9c' },
        yaxis: 'y',
    },
    {
        key: CHART_KEYS.HANDLED,
        type: 'bar',
        marker: { color: '#5384BD' },
        yaxis: 'y',
    },
    {
        key: CHART_KEYS.ABANDONMENT_RATE,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#e88b1e', width: 2, shape: 'spline' },
        yaxis: 'y2',
    },
];
export var CHART_HEIGHT_CONFIG = (_g = {},
    _g[FREQUENCY_ENUM.PERIOD] = {
        rangeDefault: [-0.5, 4.5],
        baseHeight: 442,
        overflowHeight: 478,
        barGapDefault: 0.4,
        marginBottomDefault: 80,
    },
    _g[FREQUENCY_ENUM.DAILY] = {},
    _g);
export var DAILY_VIEW_RANGE_CONFIG = (_h = {},
    _h[DAILY_FILTER_KEYS.DAILY_AVERAGE] = {
        range: [-0.5, 6.5],
        barGap: 0.5,
        marginBottom: 94,
        innerHeight: 477,
        overflowInnerHeight: 499,
    },
    _h[DAILY_FILTER_KEYS.SPECIFIC_DAY] = {
        range: [-0.5, 17.5],
        barGap: 0.2,
        marginBottom: 80,
        innerHeight: 442,
        overflowInnerHeight: 478,
    },
    _h);
var NULL_VALUE = '-';
// PERIOD TABLE
export var PERIOD_BASE_ROWS = [
    {
        render: function (value) { return value; },
    },
    {
        id: PERIOD_DATA_FIELDS.PLAN,
        styled: function (theme, value, isData) {
            if (isData === void 0) { isData = true; }
            return ({
                backgroundColor: isNil(value) && isData ? theme.palette.grey[200] : theme.palette.common.white,
            });
        },
        render: function (value, metricType, actualAttritionMode) {
            return formatNumberByLocale(value, getOptionFormat(metricType, actualAttritionMode)) || NULL_VALUE;
        },
    },
    {
        id: PERIOD_DATA_FIELDS.ACTUAL,
        styled: function (theme, value, isData) {
            if (isData === void 0) { isData = true; }
            return ({
                backgroundColor: isNil(value) && isData ? theme.palette.grey[200] : theme.palette.common.white,
            });
        },
        render: function (value, metricType, actualAttritionMode) {
            return formatNumberByLocale(value, getOptionFormat(metricType, actualAttritionMode)) || NULL_VALUE;
        },
    },
    {
        id: PERIOD_DATA_FIELDS.VARIANCE,
        styled: function (theme, value, isData) { return ({
            backgroundColor: isNil(value) && isData ? theme.palette.grey[200] : theme.palette.warning.light,
            fontWeight: 'bold',
        }); },
        render: function (value) { return formatNumberByLocale(value, getOptionFormat()) || NULL_VALUE; },
    },
];
export var PERIOD_WORK_OFFERED_HANDLED_ROWS = [
    {
        id: PERIOD_DATA_FIELDS.WORK_OFFERED_HANDLED,
        render: function (value) { return value; },
    },
    {
        id: PERIOD_DATA_FIELDS.OFFERED,
        styled: function (theme, value, isData) {
            if (isData === void 0) { isData = true; }
            return ({
                backgroundColor: isData && !value ? theme.palette.grey[200] : theme.palette.common.white,
            });
        },
        render: function (value, metricType) {
            return formatNumberByLocale(value, getOptionFormat(metricType)) || NULL_VALUE;
        },
    },
    {
        id: PERIOD_DATA_FIELDS.HANDLED,
        styled: function (theme, value, isData) {
            if (isData === void 0) { isData = true; }
            return ({
                backgroundColor: isData && !value ? theme.palette.grey[200] : theme.palette.common.white,
            });
        },
        render: function (value, metricType) {
            return formatNumberByLocale(value, getOptionFormat(metricType)) || NULL_VALUE;
        },
    },
    {
        id: PERIOD_DATA_FIELDS.ABANDONMENT_RATE,
        styled: function (theme, value, isData) { return ({
            backgroundColor: isNil(value) && isData ? theme.palette.grey[200] : theme.palette.warning.light,
            fontWeight: 'bold',
        }); },
        render: function (value) {
            return formatNumberByLocale(value, getOptionFormat(METRICS_ENUM.ABANDONMENT_RATE)) || NULL_VALUE;
        },
    },
];
export var CUSTOM_WIDTH_AUTOCOMPLETE = (_j = {},
    _j[PlanType.MonthlyCustom] = '12rem',
    _j[PlanType.Monthly] = '11rem',
    _j[PlanType.Weekly] = '15rem',
    _j);
export var DAILY_INTERVAL = {
    15: INTERVAL_TYPE.FIFTEEN_MINUTES,
    30: INTERVAL_TYPE.THIRTY_MINUTES,
    60: INTERVAL_TYPE.SIXTY_MINUTES,
};
export var RANGE_OPTIONS = {
    nTicks: 9,
    stepCandidates: [0.001, 0.002, 0.005, 0.01, 0.015, 0.02, 0.05, 0.1, 0.15, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100],
    flatPadding: { leftSteps: 3, rightSteps: 5 },
    maxNegativeStepsForVariance: 3,
    minDTickForHighRanges: 0.01,
};
